import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Megaphone, Plus, Trash2 } from "lucide-react";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";

export const AdminTicker = () => {
  const [messages, setMessages] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/ticker")
      .then(({ data }) => setMessages(data.messages))
      .catch(() => { toast.error("Failed to load ticker messages"); setMessages([]); });
  }, []);

  const patch = (idx, changes) =>
    setMessages((prev) => prev.map((m, i) => (i === idx ? { ...m, ...changes } : m)));

  const addRow = () => setMessages((prev) => [...prev, { id: "", text: "", active: true }]);
  const removeRow = (idx) => setMessages((prev) => prev.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/ticker", { messages });
      setMessages(data.messages);
      toast.success("Ticker-Tape saved — live on the store now");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save ticker");
    } finally {
      setSaving(false);
    }
  };

  if (messages === null) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#8C7B6B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const activeMsgs = messages.filter((m) => m.active && m.text.trim());
  const preview = activeMsgs.map((m) => m.text).join("      •      ");

  return (
    <div data-testid="admin-ticker-section">
      <div className="flex items-center justify-between mb-5">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#6E675F] flex items-center gap-2">
          <Megaphone className="w-4 h-4" /> Ticker-Tape
        </p>
        <Button data-testid="ticker-save-button" onClick={save} disabled={saving} className="bg-[#8C7B6B] text-white hover:bg-[#75665A]">
          {saving ? "Saving…" : "Save & Publish"}
        </Button>
      </div>

      <p className="text-xs text-[#6E675F] mb-5 max-w-2xl">
        A scrolling banner shown along the top of every store page — perfect for restock news and promos
        (e.g. "Fresh stock of RETA 20 dropped — buy 5 and get 20% off"). Only messages switched ON appear.
        Leave all messages off (or empty) to hide the banner entirely.
      </p>

      {activeMsgs.length > 0 && (
        <div className="bg-[#1A1A1A] text-[#E8DCC8] overflow-hidden rounded-lg mb-6" data-testid="ticker-preview">
          <div className="ticker-track flex whitespace-nowrap py-2" style={{ animationDuration: `${Math.max(18, Math.round(preview.length / 4))}s` }}>
            <span className="text-[11px] tracking-[0.18em] uppercase font-semibold px-10">{preview}</span>
            <span className="text-[11px] tracking-[0.18em] uppercase font-semibold px-10" aria-hidden="true">{preview}</span>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {messages.map((m, idx) => (
          <div key={m.id || `new-${idx}`} className="flex items-center gap-3 border border-[#DDD7CF] rounded-lg bg-white p-3" data-testid={`ticker-row-${idx}`}>
            <Switch
              checked={m.active}
              onCheckedChange={(checked) => patch(idx, { active: checked })}
              data-testid={`ticker-active-toggle-${idx}`}
              aria-label="Toggle message visibility"
            />
            <Input
              data-testid={`ticker-text-input-${idx}`}
              value={m.text}
              onChange={(e) => patch(idx, { text: e.target.value })}
              maxLength={200}
              placeholder="Fresh stock of RETA 20 dropped — buy 5 and get 20% off"
              className="flex-1"
            />
            <button
              data-testid={`ticker-delete-${idx}`}
              onClick={() => removeRow(idx)}
              className="text-[#B42318] hover:bg-[#FBEAEA] rounded-md p-2 transition-colors shrink-0"
              aria-label="Delete message"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
        {messages.length === 0 && (
          <p className="text-sm text-[#6E675F] py-8 text-center border border-[#DDD7CF] rounded-lg bg-white" data-testid="ticker-empty">
            No messages yet — add your first announcement below.
          </p>
        )}
      </div>

      <button
        data-testid="ticker-add-button"
        onClick={addRow}
        className="mt-4 inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase font-semibold text-[#8C7B6B] border border-[#8C7B6B] rounded-none px-5 py-2.5 hover:bg-[#8C7B6B] hover:text-white transition-colors"
      >
        <Plus className="w-4 h-4" /> Add Message
      </button>
    </div>
  );
};
